import { Fragment } from "react";
import { Loader2 } from "lucide-react";
import type { SourceUrlUIPart, UIMessage } from "ai";
import { useTranslation } from "@/hooks/useTranslation";
import { ToolApprovalCard } from "./ToolApprovalCard";
import { CreatorResults, type SearchCreatorsOutput } from "./CreatorResults";

/** Tools the user should never see fire — they are plumbing, not actions. */
const SILENT_TOOLS = new Set(["emit_progress", "load_skill"]);

interface ToolPart {
  type: string;
  state?: string;
  input?: unknown;
  output?: unknown;
  approval?: { id: string; approved?: boolean };
}

function isToolPart(part: { type: string }): part is ToolPart {
  return part.type.startsWith("tool-");
}

/**
 * The `<user_context>` / `<long_term_memory>` blocks are appended to the copy of
 * a message sent to the model and are not persisted — but a stored thread from
 * an older build might still contain them, and they must never render.
 */
function isContextBlock(text: string): boolean {
  const trimmed = text.trimStart();
  return trimmed.startsWith("<user_context>") || trimmed.startsWith("<long_term_memory>");
}

/** Minimal inline formatting, matching what the rest of the app renders. */
function renderInline(text: string) {
  return text.split(/(\*\*.*?\*\*)/g).map((chunk, i) =>
    chunk.startsWith("**") && chunk.endsWith("**") ? (
      <strong key={i}>{chunk.slice(2, -2)}</strong>
    ) : (
      <Fragment key={i}>{chunk}</Fragment>
    ),
  );
}

export interface AssistantMessageProps {
  message: UIMessage;
  /** True while this message is the one currently streaming. */
  isStreaming: boolean;
  /** Approval responses only apply to the last message in the SDK's store. */
  isLastMessage: boolean;
  projectId?: string;
  onApprovalResponse: (id: string, approved: boolean) => void;
}

export function AssistantMessage({
  message,
  isStreaming,
  isLastMessage,
  projectId,
  onApprovalResponse,
}: AssistantMessageProps) {
  const { t } = useTranslation();
  const isUser = message.role === "user";

  const sources = message.parts.filter(
    (p): p is SourceUrlUIPart => p.type === "source-url",
  );

  const hasText = message.parts.some(
    (p) => p.type === "text" && p.text.trim().length > 0,
  );

  // The most recent progress message is the live status line. It is only useful
  // until the answer itself starts arriving — after that it describes work
  // already finished.
  const progressMessages = message.parts
    .filter((p) => p.type === "tool-emit_progress")
    .map((p) => (p as ToolPart).input as { message?: string } | undefined)
    .map((input) => input?.message)
    .filter((m): m is string => typeof m === "string" && m.length > 0);

  const latestProgress = progressMessages[progressMessages.length - 1];

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`min-w-0 space-y-2.5 ${
          isUser
            ? "max-w-[280px] rounded-xl px-4 py-3 text-sm leading-relaxed bg-gradient-to-r from-steel-600 to-steel-700 text-white"
            : "w-full text-sm leading-relaxed text-neutral-700"
        }`}
      >
        {message.parts.map((part, index) => {
          if (part.type === "text") {
            if (isContextBlock(part.text)) return null;
            return (
              <p key={index} className="whitespace-pre-wrap">
                {renderInline(part.text)}
              </p>
            );
          }

          if (isToolPart(part)) {
            const toolName = part.type.slice("tool-".length);
            if (SILENT_TOOLS.has(toolName)) return null;

            const input = (part.input ?? {}) as Record<string, unknown>;

            // Creator search results render as the real directory cards rather
            // than being described in prose. The payload is the tool's own
            // query result, so the verified badge on each card is server truth
            // — not the model choosing to mention it.
            if (toolName === "search_creators" && part.state === "output-available") {
              return (
                <CreatorResults
                  key={index}
                  output={(part.output ?? {}) as SearchCreatorsOutput}
                />
              );
            }

            if (part.state === "approval-requested" && part.approval) {
              const approvalId = part.approval.id;
              // `addToolApprovalResponse` only rewrites the last message, so a
              // card further up the thread can never be answered. Render it as
              // expired rather than showing a button that does nothing.
              if (!isLastMessage) {
                return (
                  <ToolApprovalCard
                    key={index}
                    toolName={toolName}
                    input={input}
                    projectId={projectId}
                    onRespond={() => {}}
                    decided="expired"
                  />
                );
              }
              return (
                <ToolApprovalCard
                  key={index}
                  toolName={toolName}
                  input={input}
                  projectId={projectId}
                  onRespond={(approved) => onApprovalResponse(approvalId, approved)}
                />
              );
            }

            // Once answered, the card stays visible as a record of the decision.
            if (part.approval?.approved !== undefined) {
              return (
                <ToolApprovalCard
                  key={index}
                  toolName={toolName}
                  input={input}
                  projectId={projectId}
                  onRespond={() => {}}
                  decided={part.approval.approved ? "approved" : "denied"}
                />
              );
            }

            return null;
          }

          return null;
        })}

        {/* Live status line, suppressed once the answer itself starts arriving. */}
        {!isUser && isStreaming && !hasText && latestProgress && (
          <p className="flex items-center gap-2 text-xs text-neutral-400">
            <Loader2 className="w-3 h-3 animate-spin" />
            {latestProgress}
          </p>
        )}

        {sources.length > 0 && (
          <div className="pt-1">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-400">
              {t("assistant.sources")}
            </p>
            <ul className="mt-1 space-y-0.5">
              {sources.map((source, index) => (
                <li key={`${source.url}-${index}`}>
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-xs text-steel-600 hover:underline break-all"
                  >
                    {source.title || source.url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
